import {
    Container,
    Image,
    MaterialContext,
    Panel,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {
    Align,
    FlexDirection,
    Justify,
    PositionType,
    Wrap,
} from '@wonderlandengine/react-ui';

import {useState, useEffect, useContext} from 'react';
import {RotBMaterialContext} from '../utils/rotb-material-context.js';
import {ScoreController} from '../../components/score-controller.js';
import {useBombTutorial} from '../hooks/usePowerup.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {MalSpeech, TailDirection} from '../components/mal-speech.js';
import {RotatableImage} from '../components/rotatable-image.js';
import {TutorialFingerTypes} from '../../enums/TutorialFingerTypes.js';
import {useAnimation} from '../hooks/useAnimation.js';

export const TutorialHUDPanel = () => {
    const rotbMats = useContext(RotBMaterialContext);
    const HUDModel = ScoreController.instance.HUDModel;
    const mat = useContext(MaterialContext);
    const [showMaxedLevelText, setShowMaxedLevelText] = useState(HUDModel.showMaxedLevel);

    const [cooldown, inventory, inventoryColor, onClick, cooldownMat] = useBombTutorial();
    const [malSpeech, setMalSpeech] = useState('A');
    const [malSpeechVisible, setMalSpeechVisible] = useState(false);
    const [tutorialFinger, setTutorialFinger] = useState(TutorialFingerTypes.None);

    const [rotateAnimationRef, rotateSetReference] = useAnimation({
        position: {from: [-50, 0], to: [50, 0]},
        duration: 500,
        repeat: true,
        yoyo: true,
    });

    const [popAnimationRef, popSetReference] = useAnimation({
        position: {from: [0, -10], to: [0, 10]},
        scale: {from: [1.2, 1.2], to: [0.8, 0.8]},
        duration: 500,
        repeat: true,
        yoyo: true,
    });

    const [swipeAnimationRef, swipeSetReference] = useAnimation({
        position: {from: [0, 400], to: [0, 0]},
        duration: 250,
        delay: 500,
        repeatDelay: 500,
        repeat: true,
    });

    const [powerupAnimationRef, powerupSetReference] = useAnimation({
        position: {from: [-20, 0], to: [0, -20]},
        duration: 500,
        repeat: true,
        yoyo: true,
    });

    useEffect(() => {
        const onScoreModelChanged = (property: string) => {
            if (property === 'showMaxedLevel') {
                setShowMaxedLevelText(HUDModel.showMaxedLevel);
            }
            if (property === 'malSpeech') {
                setMalSpeech(HUDModel.malSpeech);
                if (HUDModel.malSpeech !== '') {
                    setMalSpeechVisible(true);
                } else {
                    setMalSpeechVisible(false);
                }
            }
            if (property === 'tutorialFinger') {
                setTutorialFinger(HUDModel.tutorialFinger);
            }
        };
        HUDModel.propertyChanged.add(onScoreModelChanged);

        return () => {
            HUDModel.propertyChanged.remove(onScoreModelChanged);
        };
    }, []);

    useEffect(() => {
        rotateSetReference(rotateAnimationRef.current);
    }, [tutorialFinger, rotateAnimationRef]);

    useEffect(() => {
        popSetReference(popAnimationRef.current);
    }, [tutorialFinger, popAnimationRef]);

    useEffect(() => {
        swipeSetReference(swipeAnimationRef.current);
    }, [tutorialFinger, swipeAnimationRef]);

    useEffect(() => {
        powerupSetReference(powerupAnimationRef.current);
    }, [tutorialFinger, powerupAnimationRef]);

    return (
        <Container
            margin={0}
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Column}
            justifyContent={Justify.FlexStart}
        >
            {tutorialFinger === TutorialFingerTypes.Powerup && (
                <Row
                    position={PositionType.Absolute}
                    alignSelf={Align.Center}
                    maxWidth={1000}
                    width="100%"
                    height="100%"
                    flexGrow={1}
                    padding={50}
                    justifyContent={Justify.FlexEnd}
                    alignItems={Align.FlexEnd}
                >
                    <Container marginRight={100} marginBottom={50} width={192} height={192}>
                        <RotatableImage
                            key="PowerupFinger"
                            ref={powerupAnimationRef}
                            width={192}
                            height={192}
                            src={UiTexturesManager.instance.finger}
                            rotation={225}
                        ></RotatableImage>
                    </Container>
                </Row>
            )}

            {tutorialFinger === TutorialFingerTypes.Swipe && (
                <Image
                    key="SwipeFinger"
                    ref={swipeAnimationRef}
                    alignSelf={Align.Center}
                    top="50%"
                    marginTop={100}
                    marginLeft={200}
                    width={160}
                    height={160}
                    src={UiTexturesManager.instance.finger}
                    position={PositionType.Absolute}
                ></Image>
            )}
            {tutorialFinger === TutorialFingerTypes.PopBlob && (
                <Image
                    key="PopBlobFinger"
                    ref={popAnimationRef}
                    alignSelf={Align.Center}
                    top="50%"
                    marginTop={100}
                    width={192}
                    height={192}
                    src={UiTexturesManager.instance.finger}
                    position={PositionType.Absolute}
                ></Image>
            )}
            {tutorialFinger === TutorialFingerTypes.Rotation && (
                <Image
                    ref={rotateAnimationRef}
                    key="RotationFinger"
                    alignSelf={Align.Center}
                    top="50%"
                    marginTop={150}
                    width={192}
                    height={192}
                    src={UiTexturesManager.instance.finger}
                    position={PositionType.Absolute}
                ></Image>
            )}
            <Row
                width="100%"
                flexDirection={FlexDirection.RowReverse}
                flexWrap={Wrap.NoWrap}
                alignContent={Align.Stretch}
                justifyContent={Justify.FlexStart}
                alignItems={Align.Stretch}
            >
                <Container width="50%"></Container>
                <Container width={50} flexGrow={0} flexShrink={0}>
                    {/* <Panel
                        rounding={0}
                        height={100}
                        width={30}
                        backgroundColor="#00ff00"
                    ></Panel> */}
                </Container>
                <Container
                    marginTop={50}
                    flexShrink={1}
                    flexGrow={1}
                    alignItems={Align.FlexEnd}
                >
                    {malSpeechVisible && (
                        // <Panel rounding={0} backgroundColor="#ff0000">
                        // <Container
                        //right={250} top={50} width={250} height={100}
                        //>
                        <MalSpeech maxWidth={300} direction={TailDirection.right}>
                            {malSpeech}
                        </MalSpeech>
                        // </Container>
                        // </Panel>
                    )}
                </Container>
            </Row>

            {/* <Row width="100%">
                <Container flexGrow={1}>
                    {malSpeechVisible && (
                        <Panel rounding={0} backgroundColor="#ff0000">
                            <Container
                            //right={250} top={50} width={250} height={100}
                            >
                                <MalSpeech direction={TailDirection.right}>
                                    {malSpeech}
                                </MalSpeech>
                            </Container>
                        </Panel>
                    )}
                </Container>
                <Container flexShrink={0} alignContent={Align.Stretch}>
                    <Panel
                        rounding={0}
                        height={300}
                        width={200}
                        backgroundColor="#00ff00"
                    ></Panel>
                </Container>
                <Container
                    flexGrow={1}
                    alignContent={Align.Stretch}
                    alignItems={Align.Stretch}
                >
                    <Panel rounding={0} backgroundColor="#0000ff"></Panel>
                </Container>
            </Row> */}
            <Row
                alignSelf={Align.Center}
                maxWidth={1000}
                width="100%"
                flexGrow={1}
                padding={50}
            >
                <Container
                    width={100}
                    flexGrow={0}
                    justifyContent={Justify.FlexEnd}
                    gap={50}
                ></Container>
                <Container
                    flexGrow={1}
                    height="75%"
                    flexDirection={FlexDirection.Column}
                    justifyContent={Justify.Center}
                    alignItems={Align.Center}
                >
                    {showMaxedLevelText && (
                        <Text
                            textAlign="center"
                            material={rotbMats?.uiTextShadowMaterial}
                            fontSize={52}
                        >
                            WINNER!
                        </Text>
                    )}
                </Container>
                <Container
                    width={100}
                    flexGrow={0}
                    justifyContent={Justify.FlexEnd}
                    gap={50}
                >
                    <Container onClick={() => onClick()}>
                        <Image
                            material={cooldownMat}
                            width={80}
                            height={80}
                            rounding={0}
                            src={UiTexturesManager.instance.buttonBuyBomb}
                        ></Image>

                        <Container
                            position={PositionType.Absolute}
                            width={80}
                            justifyContent={Justify.FlexEnd}
                            height={72}
                        >
                            <Text
                                width={72}
                                textAlign="right"
                                color={inventoryColor}
                                z={0.001}
                                fontSize={24}
                            >
                                {inventory}
                            </Text>
                        </Container>
                    </Container>
                </Container>
            </Row>
            <Row flexGrow={0}></Row>
        </Container>
    );
};
